import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, Label } from "@/components/Reveal";
import TopoBackground from "@/components/TopoBackground";
import { MEDIA } from "@/data/content";

const PORTRAIT = "https://customer-assets.emergentagent.com/job_steward-memory/artifacts/vownf23h_william%20coffee%20bio%20photo.jpg";
const IMG_DL_PHOTO = "https://customer-assets.emergentagent.com/job_steward-memory/artifacts/ymtedner_dalai_lama_tucson.jpg";
const IMG_DL_COVER = "https://customer-assets.emergentagent.com/job_steward-memory/artifacts/juaevvqi_dalai-lama-cover%202.jpg";

const CHAPTERS = [
  {
    label: "Beginnings",
    body: [
      "William's lifelong exploration of stewardship began while recording traditional stories and songs with Phillip Cassadore, the last Apache Indian medicine man, at his remote Arizona homestead.",
      "This path was later profoundly shaped through work with His Holiness the Dalai Lama, whose reflections on the inseparable relationship between culture, biodiversity and seed conservation became a defining influence on William's subsequent work in native seed conservation, ecological restoration and Indigenous knowledge alongside Aboriginal communities across Australia.",
    ],
    images: [
      { src: IMG_DL_PHOTO, caption: "With His Holiness the Dalai Lama, Tucson, Arizona." },
      { src: IMG_DL_COVER, caption: "Arising from Flames — Timeless Wisdom Series." },
    ],
  },
  {
    label: "Global Storytelling",
    body: [
      "William began his career producing educational films and multimedia programs for Tony Robbins, creating content that reached audiences around the world and explored personal transformation, leadership and human potential.",
      "He later travelled internationally as a filmmaker documenting community initiatives for The Body Shop's Trade Not Aid program, working alongside founder Anita Roddick to tell stories of Indigenous peoples, traditional knowledge holders and community enterprises. These experiences profoundly shaped his belief that the most powerful stories emerge when communities become authors of their own narratives.",
    ],
  },
  {
    label: "Indigenous Media Leadership",
    body: [
      "Building on this foundation, William moved into Indigenous media, serving as CEO of NG Media, where he worked alongside Aboriginal communities to strengthen Indigenous voices through film, broadcasting and digital media.",
      "Across remote Australia, he collaborated with Elders, Rangers and communities to support cultural preservation, community storytelling and locally led media initiatives. These experiences deepened his understanding that media is most powerful when it strengthens cultural authority, supports self-determination and preserves knowledge for future generations.",
    ],
  },
  {
    label: "Stewardship Systems",
    body: [
      "Today, William's work has expanded beyond storytelling into the design of stewardship systems that help communities care for Country, preserve knowledge and build long-term resilience.",
      "Through initiatives such as WisdomKeepers, he explores how digital archives, participatory video, oral history, ecological restoration, mapping, governance and emerging technologies can work together as part of an integrated stewardship framework. Rather than viewing these as separate disciplines, he sees them as interconnected tools that help communities document knowledge, strengthen cultural continuity, support ecological restoration and guide future decision-making.",
    ],
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-20">
      {/* Intro */}
      <section className="relative py-28 md:py-40 overflow-hidden border-b border-white/5">
        <img src={MEDIA.forestPath} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050806] via-[#050806]/85 to-[#050806]" />
        <TopoBackground opacity={0.06} />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-[1fr_340px] gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Label className="block mb-6">About</Label>
            <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.06] text-[#F4F1ED]">
              William Martin
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-[#A8A5A1] leading-relaxed">
              A producer, filmmaker, community development practitioner and stewardship
              strategist whose work over more than three decades has explored a single
              enduring question:
            </p>
            <p className="mt-6 max-w-2xl font-display font-light text-2xl md:text-3xl text-[#F4F1ED] leading-snug">
              How can storytelling strengthen our relationship with people, place and future
              generations?
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto w-full max-w-[300px] lg:max-w-none"
          >
            <div className="relative rounded-2xl overflow-hidden border border-white/10">
              <img src={PORTRAIT} alt="Portrait of William Martin" className="w-full aspect-[4/5] object-cover" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/5 rounded-2xl" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Chapters */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12 space-y-20 md:space-y-28">
          {CHAPTERS.map((c, i) => (
            <div key={c.label} className="grid md:grid-cols-[minmax(0,240px)_1fr] gap-8 md:gap-16">
              <Reveal>
                <div className="md:sticky md:top-28 self-start">
                  <span className="font-display text-lg text-[#D4A373]/50">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="mt-2 font-display font-light text-3xl text-[#F4F1ED] leading-tight">{c.label}</h2>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="space-y-5 text-lg text-[#A8A5A1] leading-relaxed">
                  {c.body.map((p, j) => <p key={j}>{p}</p>)}
                </div>
                {c.images && (
                  <div className="mt-8 flex flex-wrap gap-4">
                    {c.images.map((im) => (
                      <figure key={im.src} className="w-40 sm:w-48">
                        <div className="rounded-lg overflow-hidden border border-white/10 bg-[#0C110E]">
                          <img
                            src={im.src}
                            alt={im.caption}
                            loading="lazy"
                            className="w-full h-52 object-contain opacity-90 grayscale-[20%] hover:grayscale-0 hover:opacity-100 transition-all duration-700"
                          />
                        </div>
                        <figcaption className="mt-2 text-xs text-[#A8A5A1]/70 leading-snug">{im.caption}</figcaption>
                      </figure>
                    ))}
                  </div>
                )}
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* Guiding principle */}
      <section className="py-24 md:py-32 border-y border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <Label className="block mb-8">A guiding principle</Label>
            <p className="font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.25] text-[#F4F1ED]">
              The people who hold knowledge should also shape how that knowledge is{" "}
              <span className="text-[#D4A373]">recorded, governed and shared.</span>
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-10 max-w-2xl mx-auto text-lg text-[#A8A5A1] leading-relaxed">
              Whether through film, digital archives, participatory media or stewardship
              initiatives, his work is dedicated to helping communities create living
              knowledge systems that strengthen relationships between people, place and
              future generations.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Feather — a quiet close */}
      <section className="pt-24 md:pt-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 flex justify-center">
          <img
            src="/feather.png"
            alt="A feather — received through humility, relationship and responsibility"
            className="h-56 md:h-72 w-auto object-contain opacity-100 brightness-150 contrast-105"
          />
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <Link
              to="/partner"
              data-testid="about-cta-link"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-md bg-[#D4A373] text-[#050806] font-medium hover:bg-[#E6CCB2] transition-colors duration-500"
            >
              Start a Conversation <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
